import { useMutation } from "@apollo/client";
import { Tree } from "antd";
import { BasicDataNode } from "rc-tree";
import React, { useEffect } from "react";
import { GroupItemFragmentFragment } from "~/gql/graphql";
import { GroupItemActionList } from "./group-item-action-list";
import { CREATE_GROUP_ITEM, GET_GROUP_ITEMS, useGroupItems } from "./hooks/use-group-items";
import styles from "./group-item.css";
import { LoadingSkeleton } from "../page-loading/skeleton/loading-skeleton";

type DataNode = React.ComponentProps<typeof Tree>["treeData"];

type Props = {
  groupItemEntityType: GroupItemFragmentFragment["entityType"];
  onSelectTreeItem?: (id: number[]) => void;
  selectedTreeKeys?: number[];
};
type GroupItem = {
  id: number;
  name: string;
  parentId?: number | null;
  children?: GroupItem[] | null;
};

export const GroupItems: React.FC<Props> = ({
  groupItemEntityType,
  onSelectTreeItem,
  selectedTreeKeys,
}) => {
  const { data, loading } = useGroupItems(groupItemEntityType);
  const [createGroupItem] = useMutation(CREATE_GROUP_ITEM);

  useEffect(() => {
    if (data && !selectedTreeKeys?.length && data.groupItems.length > 0) {
      // set to the first one on first load if nothing is picked
      onSelectTreeItem && onSelectTreeItem([data.groupItems[0].id]);
    }
  }, [data]);

  const onCreate = (vals: { name: string; parentId: number }) => {
    createGroupItem({
      variables: {
        createInput: {
          name: vals.name,
          description: vals.name,
          parentId: vals.parentId,
          entityType: groupItemEntityType,
        },
      },
      refetchQueries: [
        { query: GET_GROUP_ITEMS, variables: { groupedEntityType: groupItemEntityType } },
      ],
    });
  };

  const renderTitle = (item: GroupItem, index: number) => {
    return (
      <div
        style={{
          overflow: "hidden",
          display: "flex",
          width: "100%",
          justifyContent: "space-between",
        }}
      >
        <span style={{ overflow: "hidden" }}>{item.name}</span>
        {index + 1 < 5 && (
          <GroupItemActionList
            groupEntityType={groupItemEntityType}
            onCreate={(vals) => onCreate(vals)}
            item={item}
          />
        )}
      </div>
    );
  };

  const mapRecursiveChildren = (
    parentName: string,
    children?: GroupItem[] | null
  ): DataNode | undefined => {
    return children
      ? children.map((item, i) => {
          if (!item) undefined;
          return {
            title: renderTitle(item, i),
            key: item.id,
            // key: parentName + "/" + item.name,
            isLeaf: !item.children?.length,
            selectable: true,
            ...(item.children && {
              children: mapRecursiveChildren(parentName + "/" + item.name, item.children),
            }),
          };
        })
      : undefined;
  };

  const transformedToShaped = data?.groupItems.map((item, i) => ({
    key: item.id,
    children: mapRecursiveChildren(item.name, item.children),
    isLeaf: !item.children?.length,
    title: renderTitle(item, i),
    selectable: true,
  }));

  return (
    <div className={styles["group-item-tree"]}>
      {loading && <LoadingSkeleton loading={true} count={10} />}
      {!loading && (
        <Tree
          selectedKeys={selectedTreeKeys}
          onSelect={(keys, info) => {
            console.log({ info });
            const allKeys = info.node.children?.reduce(
              (acc, item) => {
                if (item && "key" in item) {
                  acc.push(Number(item.key));
                }

                if (item && "children" in item && (item.children?.length ?? 0) > 0) {
                  recursiveChildCheck(item, info, acc);
                }

                return acc;
              },
              [Number(info.node.key)]
            );

            return onSelectTreeItem && onSelectTreeItem(allKeys ?? []);
          }}
          autoExpandParent
          itemHeight={100}
          style={{ width: "100%" }}
          treeData={transformedToShaped}
        />
      )}
    </div>
  );
};
function recursiveChildCheck(item: BasicDataNode | DataNode, info: any, acc: number[]) {
  console.log({ item });
  if (item && "children" in item) {
    info.node.children?.reduce((acc: number[], child: BasicDataNode | DataNode) => {
      if (child && "key" in child) {
        acc.push(Number(child.key));
      }
      return acc;
    }, acc);
  }
}
